import type { HikingMetadata } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { UnitToggle } from "~/components/ui/unit-toggle";
import { useInlineEditor } from "~/hooks/useInlineEditor";

type DistanceUnit = "km" | "mi";
type Distance = NonNullable<HikingMetadata["distance"]>;

interface DistanceEditorProps {
  value: Distance | null | undefined;
  onChange: (value: Distance | null) => void;
  canEdit?: boolean;
  disabled?: boolean;
}

export function DistanceEditor({ value, onChange, canEdit, disabled }: DistanceEditorProps) {
  const {
    editing,
    saving,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<Distance>({
    value,
    onSave: onChange,
    toEditState: (v) => ({
      value: v?.value?.toString() ?? "",
      unit: v?.unit ?? "km",
    }),
    fromEditState: (state) => {
      const numValue = parseFloat(state.value);
      if (!state.value.trim() || isNaN(numValue) || numValue <= 0) {
        return null;
      }
      return { value: numValue, unit: state.unit as DistanceUnit };
    },
  });

  if (editing) {
    return (
      <div ref={containerRef} className="flex gap-2 items-center" onBlur={handleBlur}>
        <Input
          ref={primaryInputRef}
          type="number"
          min="0"
          step="0.1"
          value={editState.value}
          onChange={(e) => setField("value", e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="0"
          disabled={saving}
          className="w-24 h-8 text-sm"
          aria-label="Distance"
        />
        <UnitToggle
          value={editState.unit as DistanceUnit}
          onChange={(unit) => setField("unit", unit)}
          options={["km", "mi"]}
          label="Distance unit"
          disabled={saving}
        />
        <Button size="icon-sm" variant="ghost" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={saving}>
          <Icon i="check" className="size-6 text-primary" />
        </Button>
      </div>
    );
  }

  if (value) {
    const display = (
      <span className="text-sm">
        {value.value} {value.unit}
      </span>
    );

    if (canEdit) {
      return (
        <button
          type="button"
          onClick={startEditing}
          disabled={disabled}
          className="text-foreground hover:text-primary transition-colors disabled:opacity-50"
        >
          {display}
        </button>
      );
    }

    return display;
  }

  if (canEdit) {
    return (
      <button
        onClick={startEditing}
        disabled={disabled}
        className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
      >
        + Add distance
      </button>
    );
  }

  return null;
}

interface DistanceDisplayProps {
  distance: Distance;
  className?: string;
}

export function DistanceDisplay({ distance, className }: DistanceDisplayProps) {
  return (
    <span className={cn("text-sm", className)}>
      {distance.value} {distance.unit}
    </span>
  );
}
